import {
  OreButton,
  OreCardActions,
  OreCardContainer,
  OreCardWrapper,
  OreDivider,
  OreHeading,
  OreStack,
  OreSwitch,
  OreText,
  SvgArrowLeft,
} from '@edenredespana/oreneta'
import { FormProvider, UseFormReturn } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { AutoEnrollmentDefaultConditionsModel } from '@/AutoEnrollment/domain'
import {
  Step3Actions,
  Step3GetProductDocument,
  Step3ServicesTable,
  Step3SignDocuments,
  Step3SubProductsTable,
} from '@/AutoEnrollment/ui/atoms'
import { AutoEnrollmentTranslation } from '@/AutoEnrollment/ui/autoEnrollmentTranslation'
import { useAutoEnrollment } from '@/AutoEnrollment/ui/context'
import { Step3Form } from '@/AutoEnrollment/ui/hooks'
import { edenredProducts } from '@/Shared/domain'
import { formTranslation } from '@/Shared/ui/sharedTranslation'

export const AutoEnrollmentStep3 = (): JSX.Element => {
  const { t } = useTranslation()

  const {
    getCurrentStepForm,
    documents: { model, documents, toggleProductContracted, isProductDisabled },
    previousStep,
  } = useAutoEnrollment()

  const form = getCurrentStepForm() as UseFormReturn<Step3Form>

  const OptionalWrapper = ({
    children,
    product,
  }: {
    children: JSX.Element
    product: AutoEnrollmentDefaultConditionsModel
  }): JSX.Element => {
    const premiumExists = form
      .watch('products')
      .some(p => p.productId === edenredProducts.premium)

    if (!product.productHiringOptional)
      return (
        <OreStack direction="column" space="large" placeContent="stretch">
          <OreStack direction="column" space="large" placeContent="stretch">
            <OreHeading size="headline-lg">{product.productName}</OreHeading>
            {children}
          </OreStack>
          <OreDivider space="medium" />
        </OreStack>
      )

    return (
      <OreStack direction="column" space="large" placeContent="stretch">
        <OreStack space="small" direction="column" placeContent="stretch">
          <OreHeading size="headline-lg">
            {t(AutoEnrollmentTranslation.productsConditions.contractable.title)}
          </OreHeading>
          <OreHeading size="headline-sm">
            {t(AutoEnrollmentTranslation.productsConditions.contractable.subtitle)}
          </OreHeading>
          <OreText tone="neutral-600">
            <Trans t={t}>
              {t(AutoEnrollmentTranslation.productsConditions.contractable.Wysiwyg)}
            </Trans>
          </OreText>
          <OreText tone="neutral-600">
            <Trans t={t}>
              {t(AutoEnrollmentTranslation.productsConditions.contractable.Wysiwyg2)}
            </Trans>
          </OreText>
        </OreStack>
        <div
          style={
            premiumExists
              ? { backgroundColor: '#E7F3FF', borderRadius: 'var(--radius-small)' }
              : undefined
          }
        >
          <OreCardContainer
            tone={premiumExists ? 'cobalt-100' : 'neutral-100'}
            hasShadow={premiumExists}
          >
            <OreCardWrapper>
              <OreStack direction="column" space="large" placeContent="stretch">
                <OreStack direction="row" placeContent="space-between">
                  <OreHeading
                    tone={premiumExists ? 'neutral' : 'neutral-500'}
                    size="headline-lg"
                  >
                    {product.productName}
                  </OreHeading>
                  <OreSwitch
                    label={t(
                      AutoEnrollmentTranslation.productsConditions.contractableSwitch
                        .true,
                    )}
                    onChange={() => toggleProductContracted(product.productCode, form)}
                    checked={premiumExists}
                  />
                </OreStack>
                <span>
                  <Trans t={t}>
                    {t(
                      AutoEnrollmentTranslation.productsConditions.subProductsTable
                        .description,
                    )}
                  </Trans>
                </span>
                {children}
              </OreStack>
            </OreCardWrapper>
          </OreCardContainer>
        </div>
        <OreDivider space="medium" />
      </OreStack>
    )
  }

  return (
    <FormProvider {...form}>
      <form>
        <OreStack space="large" placeContent="stretch">
          {model
            ? model.map((product: AutoEnrollmentDefaultConditionsModel, i: number) => (
                <OptionalWrapper key={`optional-wrapper-${i}`} product={product}>
                  <OreStack
                    space="large"
                    key={`product-condition-${i}`}
                    placeContent="stretch"
                  >
                    {product?.subProducts?.length ? (
                      <Step3SubProductsTable
                        key={`subproduct-condition-${i}`}
                        parentProductCode={product.productCode}
                        subProducts={product.subProducts}
                        disabledState={isProductDisabled(
                          product.productCode,
                          form.getValues('products'),
                        )}
                      />
                    ) : null}
                    {product?.services?.length ? (
                      <OreStack
                        key={`product-services-${i}`}
                        space="small"
                        placeContent="stretch"
                      >
                        <Step3ServicesTable
                          services={product.services}
                          disabledState={isProductDisabled(
                            product.productCode,
                            form.getValues('products'),
                          )}
                        />
                      </OreStack>
                    ) : null}
                    <Step3GetProductDocument
                      productId={product.productCode}
                      documentsForSigning={documents?.documentsForSigning ?? []}
                    />
                  </OreStack>
                </OptionalWrapper>
              ))
            : null}
          {documents ? (
            <OreCardContainer>
              <OreCardWrapper>
                <Step3SignDocuments />
              </OreCardWrapper>
              <OreCardActions>
                <Step3Actions />
              </OreCardActions>
            </OreCardContainer>
          ) : (
            <OreCardContainer>
              <OreCardActions>
                <OreStack space="large" direction="row" placeContent="space-between">
                  <OreButton
                    category={'tertiary'}
                    icon={<SvgArrowLeft />}
                    onClick={previousStep}
                  >
                    {t(formTranslation.common.buttons.back)}
                  </OreButton>
                </OreStack>
              </OreCardActions>
            </OreCardContainer>
          )}
        </OreStack>
      </form>
    </FormProvider>
  )
}
